<template>
  <div class="auth-callback">
    <div v-if="error" class="auth-callback__error" role="alert">
      <h1>Sign-in did not complete</h1>
      <p>{{ error }}</p>
      <button type="button" @click="retry">Try again</button>
    </div>
    <div v-else class="auth-callback__pending" role="status" aria-live="polite">
      <span class="auth-callback__spinner" aria-hidden="true"></span>
      <p>Signing you in&hellip;</p>
    </div>
  </div>
</template>

<script>
/**
 * What the user looks at while the OIDC redirect is being completed.
 *
 * It does **not** do the exchange. `bootstrapSession()` in main.js owns that,
 * because the exchange has to finish before anything mounts — this app
 * dispatches an FSM message at boot which navigates, and that used to unmount
 * this component mid-request.
 *
 * They must not both try: an authorization code is single-use, so whichever ran
 * second got a spent code and showed the user a failure after a sign-in that had
 * actually succeeded. This component now waits for the exchange bootstrapSession
 * started and reports the outcome.
 */
import oidc from '@/auth/oidcClient';
import { pendingSession } from '@/auth/bootstrapSession';
import { useSessionStore } from '@/stores/session';

export default {
  name: 'AuthCallbackPage',

  data() {
    return { error: null };
  },

  async created() {
    try {
      const inFlight = pendingSession();
      const returnTo = inFlight ? await inFlight : null;

      if (!oidc.isAuthenticated()) {
        throw new Error('Sign-in could not be completed. Please try again.');
      }

      await useSessionStore().hydrate();

      const destination = returnTo && returnTo !== '/auth/callback' ? returnTo : '/welcome';
      this.$router.replace(destination);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[auth/callback] sign-in failed:', e);
      this.error = e.message || 'Sign-in could not be completed.';
    }
  },

  methods: {
    retry() {
      this.error = null;
      oidc.clearSession();
      oidc.beginLogin({ returnTo: '/welcome' });
    },
  },
};
</script>

<style scoped>
.auth-callback {
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: 2rem;
  text-align: center;
}
.auth-callback__spinner {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  border: 3px solid rgba(0, 0, 0, 0.15);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: auth-callback-spin 0.8s linear infinite;
}
.auth-callback__error button {
  margin-top: 1rem;
  padding: 0.5rem 1.25rem;
  cursor: pointer;
}
@keyframes auth-callback-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .auth-callback__spinner { animation: none; }
}
</style>
